import { useState, useMemo } from 'react'
import { t } from '@lingui/macro'
import { useGetEstablishmentDurationMetricsQuery } from '@/core/services/statistics'
import dayjs from 'dayjs'
import { AccordionSection } from './useAccordionState'

export const useDurationMetrics = (establishmentId: string, selectedRange: [Date, Date], expandedSection: AccordionSection | false) => {
  const [showByType, setShowByType] = useState(false)
  const [selectedConsultationType, setSelectedConsultationType] = useState<string | null>(null)

  const { data: durationMetrics, isLoading: isLoadingDuration } = useGetEstablishmentDurationMetricsQuery(
    {
      establishment: establishmentId,
      startDate: dayjs(selectedRange[0]).format('YYYY-MM-DD'),
      endDate: dayjs(selectedRange[1]).format('YYYY-MM-DD'),
      includeByType: showByType,
    },
    {
      skip: !establishmentId || expandedSection !== 'duration',
      refetchOnMountOrArgChange: true,
    }
  )

  const consultationTypes = useMemo(() => {
    if (!durationMetrics?.durationDistribution?.length) return []

    const typedDistribution = durationMetrics.durationDistribution as Array<{
      range: string
      count: number
      isOutlier: boolean
      type?: string
    }>

    const uniqueTypes = Array.from(
      new Set(
        typedDistribution
          .filter((d) => d.type)
          .map((d) => d.type)
          .filter((type): type is string => !!type)
      )
    )

    return uniqueTypes
  }, [durationMetrics])

  const handleConsultationTypeChange = (event: React.SyntheticEvent, newType: string | null) => {
    setSelectedConsultationType(newType)
  }

  const handleShowByTypeChange = (checked: boolean) => {
    setShowByType(checked)
    if (!checked) {
      setSelectedConsultationType(null)
    }
  }

  const getTypeDisplayName = (type: string | null): string => {
    if (type === null) return t`All Types`

    const typeDisplayNames: Record<string, string> = {
      classic: t`Classic`,
      emergency: t`Emergency`,
      local: t`Local`,
      meeting_only: t`Meeting Only`,
      netsoins: t`Netsoins`,
      pedcl: t`PEDCL`,
    }

    return typeDisplayNames[type] || type
  }

  return {
    durationMetrics,
    isLoadingDuration,
    consultationTypes,

    showByType,
    selectedConsultationType,

    handleConsultationTypeChange,
    handleShowByTypeChange,
    getTypeDisplayName,
  }
}
